import { useState, useEffect } from 'react';

/**
 * Orientation type: portrait or landscape
 */
export type Orientation = 'portrait' | 'landscape';

/**
 * State returned by useOrientationChange hook
 */
export interface OrientationState {
    /** Current device orientation */
    orientation: Orientation;
    /** Current window width in pixels */
    width: number;
    /** Current window height in pixels */
    height: number;
    /** Whether the device is in portrait mode */
    isPortrait: boolean;
    /** Whether the device is in landscape mode */
    isLandscape: boolean;
}

/**
 * Custom hook to detect and track device orientation changes.
 * 
 * This hook listens to window resize, orientationchange events, and media queries
 * to provide real-time orientation information. It includes debouncing to prevent
 * excessive re-renders during orientation transitions.
 * 
 * **Use Case:** Fix iPad orientation change issues where screens get stuck
 * when switching between portrait and landscape modes.
 * 
 * @param debounceMs - Debounce delay in milliseconds (default: 150ms)
 * @returns OrientationState object with current orientation and dimensions
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { orientation, width, height, isPortrait } = useOrientationChange();
 *   
 *   return (
 *     <div>
 *       <p>Orientation: {orientation}</p>
 *       <p>Dimensions: {width} x {height}</p>
 *     </div>
 *   );
 * };
 * ```
 */
export const useOrientationChange = (debounceMs = 150): OrientationState => {
    // Initialize state with current window dimensions
    const [state, setState] = useState<OrientationState>(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const orientation = width > height ? 'landscape' : 'portrait';

        return {
            orientation,
            width,
            height,
            isPortrait: orientation === 'portrait',
            isLandscape: orientation === 'landscape',
        };
    });

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        /**
         * Handle orientation/resize changes with debouncing
         */
        const handleChange = () => {
            clearTimeout(timeoutId);

            timeoutId = setTimeout(() => {
                const width = window.innerWidth;
                const height = window.innerHeight;
                const newOrientation = width > height ? 'landscape' : 'portrait';

                setState({
                    orientation: newOrientation,
                    width,
                    height,
                    isPortrait: newOrientation === 'portrait',
                    isLandscape: newOrientation === 'landscape',
                });
            }, debounceMs);
        };

        // Listen to resize events (covers most cases)
        window.addEventListener('resize', handleChange);

        // Listen to orientationchange events (iOS Safari specific)
        window.addEventListener('orientationchange', handleChange);

        // Use matchMedia for more reliable orientation detection
        // This is the most reliable method for detecting orientation changes
        const portraitMediaQuery = window.matchMedia('(orientation: portrait)');
        const landscapeMediaQuery = window.matchMedia('(orientation: landscape)');

        const handleMediaChange = () => handleChange();

        // Modern browsers support addEventListener on MediaQueryList
        if (portraitMediaQuery.addEventListener) {
            portraitMediaQuery.addEventListener('change', handleMediaChange);
            landscapeMediaQuery.addEventListener('change', handleMediaChange);
        } else {
            // Fallback for older browsers (deprecated but still needed for some iOS versions)
            portraitMediaQuery.addListener(handleMediaChange);
            landscapeMediaQuery.addListener(handleMediaChange);
        }

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleChange);
            window.removeEventListener('orientationchange', handleChange);

            if (portraitMediaQuery.removeEventListener) {
                portraitMediaQuery.removeEventListener('change', handleMediaChange);
                landscapeMediaQuery.removeEventListener('change', handleMediaChange);
            } else {
                portraitMediaQuery.removeListener(handleMediaChange);
                landscapeMediaQuery.removeListener(handleMediaChange);
            }
        };
    }, [debounceMs]);

    return state;
};

/**
 * Lightweight hook that only returns the current orientation (no dimensions).
 * Use this if you only need to know portrait vs landscape.
 * 
 * @param debounceMs - Debounce delay in milliseconds (default: 150ms)
 * @returns Current orientation ('portrait' | 'landscape')
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const orientation = useOrientation();
 *   
 *   return <div>Current orientation: {orientation}</div>;
 * };
 * ```
 */
export const useOrientation = (debounceMs = 150): Orientation => {
    const { orientation } = useOrientationChange(debounceMs);
    return orientation;
};
